import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Button,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { $getNodeByKey, type NodeKey } from "lexical";
import { useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";

import { $isMergeTagNode } from "./MergeTagNode";
import { getSupportedFilterIds } from "../mergeTagDefinitions";

import {
  formatMergeTagChipLabel,
  MergeTagChip as MergeTagChipView,
} from "@/components/MergeTagChip";
import { BlueprintIcon } from "@/icons";
import {
  FALLBACK_VALUE_MAX_LENGTH,
  mergeTagsService,
  parseGeneratedContentIdentifier,
  sanitizeFallback,
  TEXT_CASE_OPTIONS,
  type ResolvedFilters,
} from "@/services/mergeTags";

/**
 * Hover copy for a generated-content chip. The value is not a customer or
 * product field but the copy the apply pipeline wrote into this tile from its
 * blueprint, so the chip says so rather than looking like any other tag.
 */
export const GENERATED_CONTENT_CHIP_TOOLTIP =
  "Generated content from this tile's blueprint";

interface MergeTagChipProps {
  nodeKey?: NodeKey;
  identifier?: string;
  label: string;
  filters?: ResolvedFilters;
}

export function MergeTagChip({
  nodeKey,
  identifier,
  label,
  filters: rawFilters,
}: MergeTagChipProps) {
  const [editor] = useLexicalComposerContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFilters, setDraftFilters] = useState<ResolvedFilters>({});

  const filters = mergeTagsService.filters.normalize(rawFilters);
  const supportedFilterIds = getSupportedFilterIds(identifier);
  const interactive =
    nodeKey !== undefined &&
    identifier !== undefined &&
    supportedFilterIds.length > 0;

  const seedDraftFilters = (): ResolvedFilters => {
    const seeded: ResolvedFilters = {};
    for (const id of supportedFilterIds) {
      if (filters[id] !== undefined) seeded[id] = filters[id];
    }
    // Not editable in the popover, but Save must not strip them.
    if (filters.passthrough !== undefined && filters.passthrough.length > 0) {
      seeded.passthrough = filters.passthrough;
    }
    return seeded;
  };

  const defaultVal =
    typeof filters.default === "string" && filters.default.length > 0
      ? filters.default
      : undefined;
  const textCase = filters.textCase;
  const textCaseLabel = TEXT_CASE_OPTIONS.find((o) => o.id === textCase)?.label;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (!interactive) return;
    event.preventDefault();
    event.stopPropagation();
    setDraftFilters(seedDraftFilters());
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const persistChanges = (nextFilters: ResolvedFilters) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey!);
      if ($isMergeTagNode(node)) {
        node.setFilters(nextFilters);
      }
    });
  };

  const handleSave = () => {
    persistChanges(mergeTagsService.filters.resolve(draftFilters));
    handleClose();
  };

  const handleRemove = () => {
    persistChanges({});
    handleClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSave();
    } else if (event.key === "Escape") {
      event.preventDefault();
      handleClose();
    }
  };

  const open = Boolean(anchorEl);
  const hasAnyFilter = Object.keys(filters).length > 0;
  const isGeneratedContent =
    identifier !== undefined &&
    parseGeneratedContentIdentifier(identifier) !== undefined;

  const chipFilterDefs = useMemo(() => {
    const allFilterDefs = mergeTagsService.filters.list();
    return allFilterDefs.filter((f) => supportedFilterIds.includes(f.id));
  }, [supportedFilterIds]);

  const chip = (
    <MergeTagChipView
      interactive={interactive}
      icon={isGeneratedContent ? BlueprintIcon : undefined}
      label={formatMergeTagChipLabel(label, filters)}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={
        interactive
          ? `Edit options for ${label}${defaultVal ? `, fallback "${defaultVal}"` : ""}${textCaseLabel ? `, text transform ${textCaseLabel}` : ""}`
          : undefined
      }
      aria-haspopup={interactive ? "dialog" : undefined}
      onClick={interactive ? handleOpen : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setDraftFilters(seedDraftFilters());
                setAnchorEl(event.currentTarget as HTMLElement);
              }
            }
          : undefined
      }
    />
  );

  return (
    <>
      {isGeneratedContent ? (
        <Tooltip title={GENERATED_CONTENT_CHIP_TOOLTIP}>{chip}</Tooltip>
      ) : (
        chip
      )}
      {interactive ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                p: 3,
                width: 320,
                backgroundColor: "background.paper-elevation-2",
              },
            },
          }}
        >
          <Stack gap={4}>
            <Typography variant="subtitle2">Options for {label}</Typography>
            {chipFilterDefs.map((filterDef, idx) => {
              if (filterDef.control === "select") {
                return (
                  <TextField
                    key={filterDef.id}
                    select
                    size="small"
                    label={filterDef.label}
                    value={
                      typeof draftFilters[filterDef.id] === "string"
                        ? (draftFilters[filterDef.id] as string)
                        : ""
                    }
                    onChange={(event) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        [filterDef.id]: event.target.value || undefined,
                      }))
                    }
                    slotProps={{
                      select: { displayEmpty: true },
                      inputLabel: { shrink: true },
                    }}
                  >
                    {(filterDef.options ?? []).map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }

              if (filterDef.control === "text") {
                return (
                  <TextField
                    key={filterDef.id}
                    autoFocus={idx === 0}
                    size="small"
                    label={filterDef.label}
                    value={
                      typeof draftFilters[filterDef.id] === "string"
                        ? (draftFilters[filterDef.id] as string)
                        : ""
                    }
                    onChange={(event) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        [filterDef.id]:
                          sanitizeFallback(event.target.value) || undefined,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                    placeholder='e.g. "there"'
                    inputProps={{ maxLength: FALLBACK_VALUE_MAX_LENGTH }}
                  />
                );
              }

              return null;
            })}
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Button
                size="small"
                color="inherit"
                onClick={handleRemove}
                disabled={!hasAnyFilter}
              >
                Reset
              </Button>
              <Stack direction="row" gap={1}>
                <Button size="small" onClick={handleClose}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleSave}>
                  Save
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Popover>
      ) : null}
    </>
  );
}
